import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Appointment,
  AppointmentExtended,
  AppointmentPatientDetails,
  AppointmentExtendedAlternate,
  AppointmentPatientDetailsAlternate,
} from 'src/models';
import { UseQueryOverrideOptions } from '../common';
import { api } from 'src/services/api';

export type UseGetAppointmentDetailsParams = {
  appt_uuid: Appointment['appt_uuid'];
  /** Only the patient timeline sets this, to open details of a deleted appointment. */
  includeDeleted?: boolean;
};

export type GetAppointmentDetailsResponse = {
  appointment_details: AppointmentExtended;
  patient_details: AppointmentPatientDetails;
};

export const useGetAppointmentDetails = (
  params: UseGetAppointmentDetailsParams,
  options: UseQueryOverrideOptions<AppointmentExtended> = {}
) => {
  const { appt_uuid, includeDeleted } = params;
  return useQuery({
    enabled: !!appt_uuid,
    ...options,
    queryKey: ['appointmentDetails', params],
    queryFn: () =>
      api
        .post<GetAppointmentDetailsResponse>(`/get_appointment/uuid`, {
          appt_uuid,
          // Only sent when truthy so existing callers are unaffected.
          include_deleted: includeDeleted || undefined,
        })
        .then(({ data }) => {
          const appointment = data?.appointment_details;
          const patient_details = data?.patient_details;
          return {
            ...appointment,
            patient_details,
          };
        }),
  });
};

export type UseGetAppointmentDetailsAlternateParams = {
  appt_uuid: Appointment['appt_uuid'];
};

export type GetAppointmentDetailsAlternateResponse = {
  appointment_details: AppointmentExtendedAlternate;
  patient_details: AppointmentPatientDetailsAlternate;
};

export const useGetAppointmentDetailsAlternate = (
  params: UseGetAppointmentDetailsAlternateParams,
  options: UseQueryOverrideOptions = {}
) => {
  const { appt_uuid } = params;
  return useQuery({
    enabled: !!appt_uuid,
    ...options,
    queryKey: ['appointmentDetailsAlternate', params],
    queryFn: () =>
      api
        .get<GetAppointmentDetailsAlternateResponse>(
          `/get_appointment/${appt_uuid}`
        )
        .then(({ data }) => {
          const appointment = data?.appointment_details;
          const patient_details = data?.patient_details;
          return {
            ...appointment,
            patient_details,
          };
        }),
  });
};

export const useAppointmentDetailsClient = () => {
  const queryClient = useQueryClient();

  const scanAppointment = (
    uuid: Appointment['appt_uuid'],
    replacer: (appointment: AppointmentExtended) => AppointmentExtended
  ) => {
    queryClient.setQueriesData<
      ReturnType<typeof useGetAppointmentDetails>['data']
    >({ queryKey: ['appointmentDetails'] }, (oldData) => {
      if (!oldData) return oldData;
      return oldData.appt_uuid === uuid ? replacer(oldData) : oldData;
    });
  };

  const invalidate = (params: UseGetAppointmentDetailsParams) => {
    queryClient.invalidateQueries({
      queryKey: ['appointmentDetails', params],
    });
    queryClient.invalidateQueries({
      queryKey: ['appointmentDetailsAlternate', params],
    });
  };

  const deleteAppointment = (params: UseGetAppointmentDetailsParams) => {
    queryClient.removeQueries({
      queryKey: ['appointmentDetails', params],
    });
    queryClient.removeQueries({
      queryKey: ['appointmentDetailsAlternate', params],
    });
  };

  return {
    scanAppointment,
    invalidate,
    deleteAppointment,
  };
};
